import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite, asset } from "@/context/SiteContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { RoleChooser } from "@/components/RoleChooser";

const CodeStep = ({ email, onBack, onNeedRole }) => {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email, code });
      setUser(data);
      toast.success("تم تسجيل الدخول");
      if (!data.role && !data.is_admin) return onNeedRole();
      nav(data.is_new || !data.phone ? "/dashboard" : location.state?.from || "/dashboard", { replace: true });
    } catch (e) { toast.error(errMsg(e)); setCode(""); } finally { setBusy(false); }
  };
  const resend = () => api.post("/auth/request-code", { email }).then(() => toast.success("تم إرسال رمز جديد")).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">أرسلنا رمزاً من 6 أرقام إلى <b dir="ltr">{email}</b></p>
      <div dir="ltr" className="flex justify-center">
        <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="login-code-input">
          <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="w-12 h-14 text-xl" />)}</InputOTPGroup>
        </InputOTP>
      </div>
      <Button data-testid="login-verify-btn" disabled={busy || code.length !== 6} onClick={verify} className="w-full h-12 rounded-lg bg-brand hover:bg-brand-dark text-base font-bold">دخول</Button>
      <div className="flex justify-between text-sm">
        <button data-testid="login-back-btn" onClick={onBack} className="flex items-center gap-1 text-slate-500 hover:text-brand"><ArrowRight size={14} />تغيير البريد</button>
        <button data-testid="login-resend-btn" onClick={resend} className="text-brand font-semibold">إعادة إرسال الرمز</button>
      </div>
    </div>
  );
};

export default function Login() {
  const { user } = useAuth();
  const site = useSite();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [needRole, setNeedRole] = useState(false);
  const nav = useNavigate();
  if (needRole) return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="login-role-step">
      <div className="bg-white border rounded-xl p-8 shadow-sm">
        <h1 className="text-2xl font-bold tracking-tight text-center mb-1">اختر نوع حسابك</h1>
        <p className="text-center text-sm text-slate-500 mb-6">يمكنك تغييره لاحقاً من لوحتي</p>
        <RoleChooser onDone={() => nav("/dashboard", { replace: true })} />
      </div>
    </div>
  );
  if (user) return <Navigate to="/dashboard" replace />;
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/request-code", { email: email.trim() }); setSent(true); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="login-page">
      <div className="bg-white border rounded-xl p-8 shadow-sm">
        <img src={asset(site.logo)} alt="" className="h-16 mx-auto mb-4" />
        <h1 className="text-2xl font-bold tracking-tight text-center mb-1">تسجيل الدخول / إنشاء حساب</h1>
        <p className="text-center text-sm text-slate-500 mb-8">أدخل بريدك وسيصلك رمز الدخول مباشرة</p>
        {sent ? <CodeStep email={email.trim()} onBack={() => setSent(false)} onNeedRole={() => setNeedRole(true)} /> : (
          <form onSubmit={send} className="space-y-4">
            <div className="relative">
              <Mail className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" dir="ltr" className="h-12 rounded-xl pr-10 text-right" />
            </div>
            <Button data-testid="login-send-code-btn" disabled={busy} type="submit" className="w-full h-12 rounded-lg bg-sun hover:bg-sun-dark text-base font-bold">{busy ? "جارِ الإرسال..." : "أرسل رمز الدخول"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
